import {
  Building,
  Check,
  ChevronsUpDown,
  MapPin,
  Radar,
  ShieldCheck,
  Tags,
  Target,
  Users,
  X,
} from "lucide-react";
import {
  FilterLiveForm,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { SelectInput } from "@/components/admin/select-input";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { Company, Sale } from "../types";
import { FilterCategory } from "../filters/FilterCategory";
import { ActiveFilterButton } from "../misc/ActiveFilterButton";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { getTranslatedCompanySizeLabel } from "./getTranslatedCompanySizeLabel";
import { sizes } from "./sizes";
import { useIsMobile } from "@/hooks/use-mobile";
import { useConfigurationContext } from "../root/ConfigurationContext";

const PRIORITY_CHOICES = ["high", "medium", "low"] as const;
const SRO_MEMBERSHIP_CHOICES = ["active", "ceased"] as const;
const DIGITAL_MATURITY_RANGES = [
  { value: "high", min: 8, max: 10 },
  { value: "medium", min: 5, max: 7 },
  { value: "low", min: 0, max: 4 },
] as const;
const CONTACT_PERMISSION_FILTERS = [
  {
    value: "granted",
    filter: { marketing_consent_status: "granted", do_not_contact: false },
  },
  {
    value: "requested",
    filter: { marketing_consent_status: "requested", do_not_contact: false },
  },
  {
    value: "unknown",
    filter: { marketing_consent_status: "unknown", do_not_contact: false },
  },
  {
    value: "revoked",
    filter: { marketing_consent_status: "revoked", do_not_contact: undefined },
  },
  {
    value: "stop_list",
    filter: { marketing_consent_status: undefined, do_not_contact: true },
  },
] as const;

const RUSSIAN_REGION_CHOICES = [
  "Алтайский край",
  "Амурская область",
  "Архангельская область",
  "Астраханская область",
  "Белгородская область",
  "Брянская область",
  "Владимирская область",
  "Волгоградская область",
  "Вологодская область",
  "Воронежская область",
  "г. Москва",
  "г. Санкт-Петербург",
  "г. Севастополь",
  "Донецкая Народная Республика",
  "Еврейская автономная область",
  "Забайкальский край",
  "Запорожская область",
  "Ивановская область",
  "Иркутская область",
  "Кабардино-Балкарская Республика",
  "Калининградская область",
  "Калужская область",
  "Камчатский край",
  "Карачаево-Черкесская Республика",
  "Кемеровская область",
  "Кировская область",
  "Костромская область",
  "Краснодарский край",
  "Красноярский край",
  "Курганская область",
  "Курская область",
  "Ленинградская область",
  "Липецкая область",
  "Луганская Народная Республика",
  "Магаданская область",
  "Московская область",
  "Мурманская область",
  "Ненецкий автономный округ",
  "Нижегородская область",
  "Новгородская область",
  "Новосибирская область",
  "Омская область",
  "Оренбургская область",
  "Орловская область",
  "Пензенская область",
  "Пермский край",
  "Приморский край",
  "Псковская область",
  "Республика Адыгея",
  "Республика Алтай",
  "Республика Башкортостан",
  "Республика Бурятия",
  "Республика Дагестан",
  "Республика Ингушетия",
  "Республика Калмыкия",
  "Республика Карелия",
  "Республика Коми",
  "Республика Крым",
  "Республика Марий Эл",
  "Республика Мордовия",
  "Республика Саха (Якутия)",
  "Республика Северная Осетия - Алания",
  "Республика Татарстан",
  "Республика Тыва",
  "Республика Хакасия",
  "Ростовская область",
  "Рязанская область",
  "Самарская область",
  "Саратовская область",
  "Сахалинская область",
  "Свердловская область",
  "Смоленская область",
  "Ставропольский край",
  "Тамбовская область",
  "Тверская область",
  "Томская область",
  "Тульская область",
  "Тюменская область",
  "Удмуртская Республика",
  "Ульяновская область",
  "Хабаровский край",
  "Ханты-Мансийский автономный округ - Югра",
  "Херсонская область",
  "Челябинская область",
  "Чеченская Республика",
  "Чувашская Республика",
  "Чукотский автономный округ",
  "Ямало-Ненецкий автономный округ",
  "Ярославская область",
];

export const CompanyListFilter = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { companySpecializations } = useConfigurationContext();
  const translatedSizes = sizes.map((size) => ({
    ...size,
    name: getTranslatedCompanySizeLabel(size, translate),
  }));
  const { data: companies } = useGetList<Company>("companies", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "state_abbr", order: "ASC" },
    filter: {},
  });
  const regionChoices = useMemo(
    () =>
      [
        ...new Set(
          [
            ...RUSSIAN_REGION_CHOICES,
            ...(companies?.map((company) => company.state_abbr?.trim()) ?? []),
          ].filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b, "ru")),
    [companies],
  );
  const { data: sales, isPending: isSalesPending } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { "disabled@neq": true },
  });
  const salesChoices = useMemo(
    () =>
      sales?.map((sale) => ({
        id: String(sale.id),
        name: `${sale.first_name} ${sale.last_name}`.trim() || sale.email,
      })) ?? [],
    [sales],
  );

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.companies.filters.search", {
          _: "Search companies",
        }),
      }}
    >
      <FilterCategory
        icon={<ShieldCheck className="h-4 w-4" />}
        label="resources.companies.filters.contact_permission"
      >
        {CONTACT_PERMISSION_FILTERS.map((choice) => (
          <ToggleFilterButton
            className="h-10 w-full justify-between md:h-8"
            label={`resources.companies.consent_status.${choice.value}`}
            key={choice.value}
            value={choice.filter}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Building className="h-4 w-4" />}
        label="resources.companies.filters.sro_membership_status"
      >
        {SRO_MEMBERSHIP_CHOICES.map((status) => (
          <ToggleFilterButton
            className="h-10 w-full justify-between md:h-8"
            label={`resources.companies.sro_membership.${status}`}
            key={status}
            value={{ sro_membership_status: status }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Target className="h-4 w-4" />}
        label="resources.companies.filters.sales_priority"
      >
        {PRIORITY_CHOICES.map((priority) => (
          <ToggleFilterButton
            className="h-10 w-full justify-between md:h-8"
            label={`resources.companies.priority.${priority}`}
            key={priority}
            value={{ lead_priority: priority }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Tags className="h-4 w-4" />}
        label="resources.companies.filters.specialization"
      >
        <FilterLiveForm>
          <SelectInput
            source="specializations@cs"
            choices={companySpecializations.map((choice) => ({
              id: `{"${choice.value}"}`,
              name: choice.label,
            }))}
            label={false}
            helperText={false}
            emptyText={translate(
              "resources.companies.filters.all_specializations",
            )}
            parse={parseEmptyFilter}
            className="w-full"
          />
        </FilterLiveForm>
      </FilterCategory>

      <FilterCategory
        icon={<Radar className="h-4 w-4" />}
        label="resources.companies.filters.sales_signals"
      >
        <ToggleFilterButton
          className="h-10 w-full justify-between md:h-8"
          label="resources.companies.filters.with_social_profiles"
          value={{ "social_profile_count@gte": 1 }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="h-10 w-full justify-between md:h-8"
          label="resources.companies.filters.with_cases_clients"
          value={{ has_cases_clients: true }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="h-10 w-full justify-between md:h-8"
          label="resources.companies.filters.with_vacancy_signal"
          value={{ has_vacancy_signal: true }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="h-10 w-full justify-between md:h-8"
          label="resources.companies.filters.with_reachable_website"
          value={{ website_status: "success" }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      <FilterCategory
        icon={<Radar className="h-4 w-4" />}
        label="resources.companies.filters.digital_maturity"
      >
        {DIGITAL_MATURITY_RANGES.map((range) => (
          <ToggleFilterButton
            className="h-10 w-full justify-between md:h-8"
            label={`resources.companies.digital_maturity.${range.value}`}
            key={range.value}
            value={{
              "digital_maturity_score@gte": range.min,
              "digital_maturity_score@lte": range.max,
            }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<MapPin className="h-4 w-4" />}
        label="resources.companies.fields.state_abbr"
      >
        <RegionSearchFilter regions={regionChoices} />
      </FilterCategory>

      <FilterCategory
        icon={<Building className="h-4 w-4" />}
        label="resources.companies.filters.auditors_count"
      >
        {translatedSizes.map((size) => (
          <ToggleFilterButton
            className="w-full justify-between h-10 md:h-8"
            label={size.name}
            key={size.name}
            value={{
              size: undefined,
              "size@gte": size.min,
              "size@lte": size.max,
            }}
            size={isMobile ? "lg" : undefined}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<Users className="h-4 w-4" />}
        label="resources.companies.fields.sales_id"
      >
        <FilterLiveForm>
          <SelectInput
            source="sales_id"
            choices={salesChoices}
            isPending={isSalesPending}
            label={false}
            helperText={false}
            emptyText={translate("resources.companies.filters.all_managers")}
            parse={parseEmptyFilter}
            className="w-full"
          />
        </FilterLiveForm>
      </FilterCategory>
    </ResponsiveFilters>
  );
};

export const CompanyListFilterSummary = () => {
  const translate = useTranslate();
  const { companySpecializations } = useConfigurationContext();
  const translatedSizes = sizes.map((size) => ({
    ...size,
    name: getTranslatedCompanySizeLabel(size, translate),
  }));
  const { filterValues } = useListContext();
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { "disabled@neq": true },
  });
  const hasFilters = !!Object.entries(filterValues || {}).filter(
    ([key]) => key !== "q",
  ).length;

  if (!hasFilters) return null;

  const selectedSale = sales?.find(
    (sale) => String(sale.id) === String(filterValues?.sales_id),
  );
  const selectedSaleName = selectedSale
    ? `${selectedSale.first_name} ${selectedSale.last_name}`.trim() ||
      selectedSale.email
    : undefined;
  const selectedRegion =
    typeof filterValues?.state_abbr === "string"
      ? filterValues.state_abbr
      : undefined;
  const selectedSpecialization = companySpecializations.find(
    (choice) => filterValues?.["specializations@cs"] === `{"${choice.value}"}`,
  );

  return (
    <div className="mb-4 flex flex-wrap items-start gap-1">
      {CONTACT_PERMISSION_FILTERS.map((choice) => (
        <ActiveFilterButton
          className="h-8 w-auto justify-between"
          key={choice.value}
          label={`resources.companies.consent_status.${choice.value}`}
          value={choice.filter}
        />
      ))}
      {PRIORITY_CHOICES.map((priority) => (
        <ActiveFilterButton
          className="h-8 w-auto justify-between"
          key={priority}
          label={`resources.companies.priority.${priority}`}
          value={{ lead_priority: priority }}
        />
      ))}
      {SRO_MEMBERSHIP_CHOICES.map((status) => (
        <ActiveFilterButton
          className="h-8 w-auto justify-between"
          key={status}
          label={`resources.companies.sro_membership.${status}`}
          value={{ sro_membership_status: status }}
        />
      ))}
      {selectedSpecialization ? (
        <ActiveFilterButton
          className="h-8 w-auto justify-between"
          label={selectedSpecialization.label}
          value={{
            "specializations@cs": `{"${selectedSpecialization.value}"}`,
          }}
        />
      ) : null}
      <ActiveFilterButton
        className="h-8 w-auto justify-between"
        label="resources.companies.filters.with_social_profiles"
        value={{ "social_profile_count@gte": 1 }}
      />
      <ActiveFilterButton
        className="h-8 w-auto justify-between"
        label="resources.companies.filters.with_cases_clients"
        value={{ has_cases_clients: true }}
      />
      <ActiveFilterButton
        className="h-8 w-auto justify-between"
        label="resources.companies.filters.with_vacancy_signal"
        value={{ has_vacancy_signal: true }}
      />
      <ActiveFilterButton
        className="h-8 w-auto justify-between"
        label="resources.companies.filters.with_reachable_website"
        value={{ website_status: "success" }}
      />
      {DIGITAL_MATURITY_RANGES.map((range) => (
        <ActiveFilterButton
          className="h-8 w-auto justify-between"
          key={range.value}
          label={`resources.companies.digital_maturity.${range.value}`}
          value={{
            "digital_maturity_score@gte": range.min,
            "digital_maturity_score@lte": range.max,
          }}
        />
      ))}
      {selectedRegion ? (
        <ActiveFilterButton
          className="w-auto justify-between h-8"
          label={selectedRegion}
          value={{ state_abbr: selectedRegion }}
        />
      ) : null}
      {translatedSizes.map((size) => (
        <ActiveFilterButton
          className="w-auto justify-between h-8"
          key={size.name}
          label={size.name}
          value={{
            size: undefined,
            "size@gte": size.min,
            "size@lte": size.max,
          }}
        />
      ))}
      {selectedSaleName ? (
        <ActiveFilterButton
          className="w-auto justify-between h-8"
          label={selectedSaleName}
          value={{ sales_id: filterValues.sales_id }}
        />
      ) : null}
    </div>
  );
};

const parseEmptyFilter = (value: unknown) => value || undefined;

const RegionSearchFilter = ({ regions }: { regions: string[] }) => {
  const translate = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const [open, setOpen] = useState(false);
  const selectedRegion =
    typeof filterValues?.state_abbr === "string"
      ? filterValues.state_abbr
      : undefined;

  useEffect(() => {
    if (!("city" in (filterValues ?? {}))) return;
    const nextFilters = { ...filterValues };
    delete nextFilters.city;
    setFilters(nextFilters);
  }, [filterValues, setFilters]);

  const setRegion = (region?: string) => {
    const nextFilters = { ...filterValues };
    delete nextFilters.city;
    if (region) {
      nextFilters.state_abbr = region;
    } else {
      delete nextFilters.state_abbr;
    }
    setFilters(nextFilters);
    setOpen(false);
  };

  return (
    <div className="flex w-full gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="min-w-0 flex-1 justify-between h-auto py-1.75 px-3 font-normal"
          >
            <span
              className={cn(
                "truncate",
                !selectedRegion && "text-muted-foreground",
              )}
            >
              {selectedRegion ??
                translate("resources.companies.filters.search_region")}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0">
          <Command>
            <CommandInput
              placeholder={translate(
                "resources.companies.filters.search_region",
              )}
            />
            <CommandList>
              <CommandEmpty>
                {translate("resources.companies.filters.no_regions")}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value={translate("resources.companies.filters.all_regions")}
                  onSelect={() => setRegion()}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !selectedRegion ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {translate("resources.companies.filters.all_regions")}
                </CommandItem>
                {regions.map((region) => (
                  <CommandItem
                    key={region}
                    value={region}
                    keywords={[region]}
                    onSelect={() => setRegion(region)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedRegion === region ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {region}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selectedRegion ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={translate("ra.navigation.clear_filters", {
            _: "Clear filters",
          })}
          onClick={() => setRegion()}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
};
